import { useEffect, useMemo } from 'react'
import useGlobalSearchContext from 'hooks/GlobalSearch/useGlobalSearchContext'
import { getHotelsTrendingDestinations, getIsFetchingTrendingDestinations } from 'selectors/destinationSelectors'
import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'

import type { SearchResult } from '../useGlobalSearchTracking'
import { SearchResultBucket, useGlobalSearchResultsTracking } from '../useGlobalSearchTracking'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { EXPERIENCE_PRODUCT_TYPES, EXPERIENCE_SEARCH_TYPES } from 'constants/experience'
import { useExperienceNaturalLanguageSearchEnabled } from 'hooks/Experiences/useExperienceNaturalLanguageSearch'
import { getExperienceNaturalLanguageItemFromUrl, isExperienceNaturalLanguageItem } from 'lib/search/experienceNaturalLanguageSearch'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getSearchItemFromURLSearchParams } from 'lib/url/searchUrlUtils'
import { fetchTrendingDestinations } from 'actions/DestinationAlertsActions'
import getOfferListKey from 'lib/offer/offerListKey'
import { GLOBAL_EXPERIENCES_SEARCH_INITIAL_STATE } from 'contexts/Experience/GlobalSearchState'
import moment from 'moment'

export function useGlobalExperiencesSearchContext(
  initialState: GlobalSearchState = GLOBAL_EXPERIENCES_SEARCH_INITIAL_STATE,
  syncWithURLSearchParams?: URLSearchParams,
) {
  const naturalLanguageEnabled = useExperienceNaturalLanguageSearchEnabled({
    queryParams: syncWithURLSearchParams,
  })
  const queryParams = useMemo(() => {
    if (syncWithURLSearchParams) {
      const checkIn = syncWithURLSearchParams.get('checkIn')
      const checkOut = syncWithURLSearchParams.get('checkOut')

      return {
        searchItem: (naturalLanguageEnabled ? getExperienceNaturalLanguageItemFromUrl(syncWithURLSearchParams) : undefined) ?? getSearchItemFromURLSearchParams(syncWithURLSearchParams),
        checkIn: checkIn && moment(checkIn).isValid() ? moment(checkIn) : undefined,
        checkOut: checkOut && moment(checkOut).isValid() ? moment(checkOut) : undefined,
      }
    }
    return undefined
  }, [syncWithURLSearchParams, naturalLanguageEnabled])

  const categoriesKeyFromUrl = useMemo(
    () => syncWithURLSearchParams?.get('categories') ?? '',
    [syncWithURLSearchParams],
  )

  const { globalSearchState, globalSearchDispatch } = useGlobalSearchContext({
    ...initialState,
    searchVerticals: new Set(['experiences']),
  })

  // sync search item and dates
  useEffect(() => {
    if (queryParams) {
      const { searchItem, checkIn, checkOut } = queryParams
      if (EXPERIENCE_SEARCH_TYPES.includes(searchItem?.searchType as App.SearchType) || (naturalLanguageEnabled && isExperienceNaturalLanguageItem(searchItem))) {
        globalSearchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_ITEM, searchItem })
      } else {
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_SEARCH_ITEM })
      }

      if (checkIn) {
        globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: checkIn })
      } else {
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
      }

      if (checkOut) {
        globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: checkOut })
      } else if (checkIn) {
        globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: checkIn })
      } else {
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams?.searchItem, queryParams?.checkIn, queryParams?.checkOut, naturalLanguageEnabled])

  useEffect(() => {
    if (!syncWithURLSearchParams) return
    const categories = categoriesKeyFromUrl
      ? categoriesKeyFromUrl.split(',').map(Number).filter(Boolean)
      : []
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_EXPERIENCE_CATEGORIES, categories })
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesKeyFromUrl])

  const dispatch = useAppDispatch()

  const isFetchingFeaturedDestinations = useAppSelector(getIsFetchingTrendingDestinations)
  const trendingDestinations = useAppSelector(getHotelsTrendingDestinations)

  useEffect(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.UNPOPULATE_POPULAR_DESTINATIONS })
    dispatch(fetchTrendingDestinations())
  }, [dispatch, globalSearchDispatch])

  useEffect(() => {
    if (!isFetchingFeaturedDestinations) {
      globalSearchDispatch({
        type: GlobalSearchStateActions.POPULATE_POPULAR_DESTINATIONS,
        popularDestinations: trendingDestinations,
      })
    }
  }, [isFetchingFeaturedDestinations, trendingDestinations, globalSearchDispatch])

  return {
    globalExperiencesSearchDispatch: globalSearchDispatch,
    globalExperiencesSearchState: globalSearchState,
  }
}

export function useGlobalExperiencesSearchResultsTracking(
  globalExperiencesSearchState: GlobalSearchState,
  experiencesIds: Array<string>,
  areResultsFetching: boolean,
) {
  const filters = useAppSelector(getUrlOfferListFilters)
  const searchParams = { ...filters, offerRypes: EXPERIENCE_PRODUCT_TYPES }
  const offerListKey = getOfferListKey(searchParams)

  const trackableResults = useMemo<Array<SearchResult>>(() => {
    const results: Array<SearchResult> = experiencesIds.map((id) => {
      const kind = id.startsWith('led:') ? SearchResultBucket.LED_EXPERIENCE : SearchResultBucket.REZDY_EXPERIENCE

      return { id, kind }
    })

    return results
  }, [experiencesIds])

  useGlobalSearchResultsTracking(
    { ...globalExperiencesSearchState, occupancies: undefined, isFlexibleDateSelected: undefined },
    offerListKey,
    trackableResults,
    areResultsFetching,
  )
}
